import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Checkbox,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Tooltip,
  FormHelperText,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { RefundDialogData, RefundServiceRow } from './refundDialogData';
import CloseIconButton from '../../common/CloseIconButton';
import NoDataRow from '../../common/NoDataRow';
import { ConfirmationModal } from '../../confirmationModal';
import { useAlert } from '../../alertProvider';
import {
  CHECKED_ICON,
  UNCHECKED_ICON,
} from '../../../pages/Billing/Invoice/common/InvoiceTableCommon';
import { TABLE_HEADER_ROW_SX } from '../../../utils/typographyStyles';
import {
  useCreateInvoiceRefund,
  buildInvoiceRefundParams,
} from '../../../pages/Billing/Invoice/invoiceHelper';
import {
  useBillingConfigOptions,
  useInvoiceDetails,
} from '../../../pages/Billing/Invoice/newInvoice/newInvoiceForm.helper';

// Table column definition keys for i18n
const TABLE_COLUMN_KEYS = [
  { id: 'serviceCode', i18nKey: 'invoice.refund.columns.service_code' },
  { id: 'serviceName', i18nKey: 'invoice.refund.columns.service_name' },
  { id: 'rate', i18nKey: 'invoice.refund.columns.rate' },
  { id: 'concession', i18nKey: 'invoice.refund.columns.concession' },
  { id: 'netAmount', i18nKey: 'invoice.refund.columns.net_amount' },
  { id: 'cancelReason', i18nKey: 'invoice.refund.columns.cancel_reason' },
] as const;


// For accessibility live error region
const LiveMessage = ({ message }: { message: string }) =>
  (
    <Box role="alert" aria-live="polite" sx={{ minHeight: 22, mt: 0.5 }}>
      <FormHelperText error={!!message}>{message}</FormHelperText>
    </Box>
  );

export interface RefundDialogProps {
  open: boolean;
  onClose: () => void;
  /** @deprecated Pass invoiceId instead - data will be fetched automatically */
  data?: RefundDialogData;
  invoiceId?: number | null;
  patientId?: string | number | null;
  patientName?: string;
  onSuccess?: () => void;
  onProceed?: (selected: RefundServiceRow[], remark: string) => void;
}

const RefundDialog: React.FC<RefundDialogProps> = ({
  open,
  onClose,
  data,
  invoiceId,
  patientId,
  patientName,
  onSuccess,
  onProceed,
}) => {
  // State
  const [rows, setRows] = useState<RefundServiceRow[]>([]);
  const [remark, setRemark] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [inputErrors, setInputErrors] = useState<{ remark?: string; perRow?: Record<string, string> }>({});
  const [invoiceInfo, setInvoiceInfo] = useState<{ invoiceName: string; invoiceNo: string }>({
    invoiceName: '',
    invoiceNo: '',
  });

  const firstFieldRef = useRef<HTMLInputElement>(null);
  const { addAlert } = useAlert();
  const { t: tBilling } = useTranslation('billing_config');

  // Fetch invoice details when dialog opens (if invoiceId is provided)
  const { data: invoiceDetails, isLoading: isLoadingInvoice } = useInvoiceDetails(
    open && invoiceId ? invoiceId : undefined
  );

  // Derive patient ID from props or invoice details for fetching billing config
  const effectivePatientId = patientId ?? invoiceDetails?.patient ?? undefined;

  // Fetch refund reasons: prefer API, fallback to data?.cancelReasons
  const { data: billingConfigData } = useBillingConfigOptions(effectivePatientId);
  const reasons = useMemo(() => {
    if (billingConfigData?.CANCEL_REASON?.length) {
      return billingConfigData.CANCEL_REASON.map(r => r.name);
    }
    return data?.cancelReasons || [];
  }, [billingConfigData, data?.cancelReasons]);
  
  // Map API response to rows when invoice details are fetched
  useEffect(() => {
    if (open && invoiceDetails && Array.isArray(invoiceDetails.services)) {
      const mappedServices: RefundServiceRow[] = invoiceDetails.services.map((svc) => ({
        id: String(svc.id),
        serviceCode: svc.billing_source_code || '',
        serviceName: svc.billing_source_name || '',
        rate: Number(svc.charges) || 0,
        concessionAmount: Number(svc.concession_amount) || 0,
        concessionPercent: Number(svc.concession_percentage) || 0,
        netAmount: Number(svc.net_amount) || 0,
        cancelReason: svc.refund_reason || '',
        refundable: false,
        is_refunded: svc.is_refunded || false,
        status_of_refund: svc.status_of_refund || '',
        is_package_closed: svc.is_package_closed || false,
      }));
      setRows(mappedServices);
      setInvoiceInfo({
        invoiceName: patientName || '',
        invoiceNo: invoiceDetails.bill_number || '',
      });
    }
  }, [open, invoiceDetails, patientName]);
  // Fallback: Initialize rows from props data (legacy support)
  useEffect(() => {
    if (open && !invoiceId && data?.services && data.services.length > 0) {
      const currentIds = rows.map(r => r.id).sort().join(',');
      const newIds = data.services.map(s => s.id).sort().join(',');
      
      if (rows.length === 0 || currentIds !== newIds) {
        setRows(data.services);
        setInvoiceInfo({
          invoiceName: data.invoiceName || '',
          invoiceNo: data.invoiceNo || '',
        });
      }
    }
  }, [open, invoiceId, data, rows]);

  // Reset forms when dialog opens
  useEffect(() => {
    if (open) {
      setRemark('');
      setInputErrors({});
      // Set focus to first field for accessibility
      setTimeout(() => {
        if (firstFieldRef.current) firstFieldRef.current.focus();
      }, 200);
    } else {
      // Reset state when dialog closes
      setRows([]);
      setInvoiceInfo({ invoiceName: '', invoiceNo: '' });
    }
  }, [open]);

  // Selection memo
  const { isAllSelected, isIndeterminate, selectedCount, selectedRows } = useMemo(() => {
    const nonRefundedRows = rows.filter(r => !r.is_refunded && !r.is_package_closed);
    const sel = rows.filter(r => r.refundable && !r.is_refunded && !r.is_package_closed);
    return {
      isAllSelected: nonRefundedRows.length > 0 && sel.length === nonRefundedRows.length,
      isIndeterminate: sel.length > 0 && sel.length < nonRefundedRows.length,
      selectedCount: sel.length,
      selectedRows: sel,
    };
  }, [rows]);

  // Refund API mutation
  const refundMutation = useCreateInvoiceRefund();

  // Handler: toggle all
  const handleToggleAll = useCallback(() => {
    setRows(prev => prev.map(r => 
      (r.is_refunded || r.is_package_closed) ? r : { ...r, refundable: !isAllSelected }
    ));
  }, [isAllSelected]);

  // Handler: toggle row
  const handleToggleRow = useCallback((id: string, isDisabled: boolean) => {
    if (isDisabled) return; // Don't allow toggling refunded or package closed services
    setRows(prev =>
      prev.map(r => (r.id === id ? { ...r, refundable: !r.refundable } : r)),
    );
  }, []);

  // Handler: change cancel reason
  const handleChangeReason = useCallback((id: string, reason: string) => {
    setRows(prev =>
      prev.map(r => (r.id === id ? { ...r, cancelReason: reason } : r)),
    );
    // Clear validation error for this row
    setInputErrors(errors => ({
      ...errors,
      perRow: { ...errors.perRow, [id]: '' },
    }));
  }, []);

  // Handler: cancellation remark
  const handleRemarkChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRemark(e.target.value);
    setInputErrors(errors => ({ ...errors, remark: '' }));
  }, []);

  // Validation before API/confirm
  const validate = useCallback((): boolean => {
    let hasError = false;
    // At least one service selected
    if (selectedCount === 0) {
      setInputErrors({ remark: '', perRow: {} });
      return false;
    }
    const perRow: Record<string, string> = {};
    selectedRows.forEach(r => {
      if (!r.cancelReason) {
        perRow[r.id] = tBilling('invoice.refund.reason_required');
        hasError = true;
      }
    });
    setInputErrors({ remark: '', perRow });
    return !hasError;
  }, [selectedRows, selectedCount, tBilling]);

  // Show confirmation
  const handleProceedClick = useCallback(() => {
    if (validate()) setConfirmOpen(true);
  }, [validate]);

  // Actual refund API call after confirm
  const handleConfirmRefund = useCallback(() => {
    setConfirmOpen(false);
    if (selectedRows.length === 0) return;
    // If invoiceId is provided, call the API
    if (invoiceId) {
      const payload = {
        remark,
        items: selectedRows.map((row) => ({
          invoice_item_id: Number(row.id),
          refund_reason: row.cancelReason,
          is_refunded: true,
        })),
      };
      refundMutation.mutate(buildInvoiceRefundParams(payload), {
        onSuccess: () => {
          onSuccess?.();
          addAlert(tBilling('invoice.refund.refund_success'), 'success');
          onClose();
        },
        onError: (error) => {
          addAlert(tBilling('invoice.refund.refund_error'), 'error');
          // eslint-disable-next-line no-console
          console.error(tBilling('invoice.refund.refund_error'), error);
        },
      });
    } else {
      // Fallback to legacy onProceed
      onProceed?.(selectedRows, remark);
      onClose();
    }
  }, [selectedRows, remark, invoiceId, refundMutation, onSuccess, onClose, onProceed, addAlert, tBilling]);

  const isProceedDisabled =
    selectedCount === 0 || refundMutation.isPending || selectedRows.some(row => !row.cancelReason);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      data-testid="refund-dialog-modal"
      PaperProps={{
        sx: { borderRadius: 3, p: 0, maxHeight: { xs: '98vh', sm: '95vh' } },
      }}
      aria-labelledby="refund-dialog-title"
      aria-modal="true"
      onKeyDown={e => {
        // trap Escape on modal only (if not on confirmation)
        if (e.key === 'Escape' && !confirmOpen) onClose();
      }}
    >
      {/* Header */}
      <DialogTitle id="refund-dialog-title" sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 18 }}>
            {tBilling('invoice.refund.title')}
          </Typography>
          <Tooltip title={tBilling('invoice.refund.close')}>
            <span>
              <CloseIconButton onClick={onClose} aria-label={tBilling('invoice.refund.close')} data-testid="refund-dialog-close" />
            </span>
          </Tooltip>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ px: 3, pt: 0, pb: 2 }}>
        {/* Loading state */}
        {isLoadingInvoice ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {/* Invoice summary */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5, color: '#1F2937' }}>
                {tBilling('invoice.refund.patient_name_prefix')} {invoiceInfo.invoiceName}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: '#1F2937' }}>
                {tBilling('invoice.refund.invoice_prefix')} {invoiceInfo.invoiceNo || '-'}
              </Typography>
            </Box>

            {/* Table */}
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                border: '1px solid #E5E7EB',
                overflowX: 'auto',
                maxHeight: { xs: 270, sm: 350, md: 500 },
                transition: 'max-height .25s',
              }}
            >
              <Table
                size="small"
                sx={{
                  minWidth: 760,
                  '& th, & td': { borderBottom: '1px solid #F2F4F7' },
                }}
                aria-label={tBilling('invoice.refund.service_list_label')}
              >
                <TableHead>
                  <TableRow sx={TABLE_HEADER_ROW_SX}>
                    <TableCell sx={{ width: 48 }}>
                      <Tooltip title={tBilling('invoice.refund.select_all')} arrow>
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={handleToggleAll}
                          icon={UNCHECKED_ICON}
                          checkedIcon={CHECKED_ICON}
                          disableRipple
                          data-testid="refund-dialog-select-all"
                          slotProps={{ input: { 'aria-label': tBilling('invoice.refund.select_all'), tabIndex: 0 } }}
                        />
                      </Tooltip>
                    </TableCell>
                    {TABLE_COLUMN_KEYS.map(col => (
                      <TableCell key={col.id}>
                        {tBilling(col.i18nKey)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <NoDataRow message={tBilling('invoice.refund.no_services')} colSpan={TABLE_COLUMN_KEYS.length + 1} />
                  ) : (
                    rows.map((row, idx) => {
                      const isRowDisabled = !!(row.is_refunded || row.is_package_closed);
                      return (
                        <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#FAFAFA' }, opacity: isRowDisabled ? 0.7 : 1 }}>
                          <TableCell sx={{ width: 48 }}>
                            <Tooltip title={row.is_refunded ? tBilling('invoice.refund.already_refunded') : row.is_package_closed ? tBilling('invoice.refund.package_closed', { defaultValue: 'Package Closed' }) : tBilling('invoice.refund.select_row')} arrow>
                              <Checkbox
                                checked={row.refundable}
                                onChange={() => handleToggleRow(row.id, isRowDisabled)}
                                icon={UNCHECKED_ICON}
                                checkedIcon={CHECKED_ICON}
                                disableRipple
                                disabled={isRowDisabled}
                                slotProps={{
                                  input: {
                                    ref: idx === 0 && !isRowDisabled ? firstFieldRef : undefined,
                                    tabIndex: isRowDisabled ? -1 : 0,
                                    'aria-checked': !!row.refundable,
                                    'aria-label': `${tBilling('invoice.refund.select_row')} ${row.serviceName || ''}`,
                                  },
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.serviceCode}</Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Box>
                              <Typography variant="body2" noWrap>
                                {row.serviceName}
                              </Typography>
                              {row.is_refunded && row.status_of_refund === 'REFUNDED' && (
                                <Chip
                                  label={tBilling('invoice.refund.refunded')}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#D1FAE5',
                                    color: '#065F46',
                                    fontWeight: 500,
                                    fontSize: 11,
                                    height: 20,
                                    mt: 0.5,
                                  }}
                                />
                              )}
                              {row.is_refunded && row.status_of_refund !== 'REFUNDED' && (
                                <Chip
                                  label={tBilling('invoice.refund.refund_requested')}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#FEF3C7',
                                    color: '#92400E',
                                    fontWeight: 500,
                                    fontSize: 11,
                                    height: 20,
                                    mt: 0.5,
                                  }}
                                />
                              )}
                              {row.is_package_closed && (
                                <Chip
                                  label={tBilling('invoice.refund.package_closed', { defaultValue: 'Package Closed' })}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#FEE2E2',
                                    color: '#991B1B',
                                    fontWeight: 500,
                                    fontSize: 11,
                                    height: 20,
                                    mt: 0.5,
                                    ml: row.is_refunded ? 0.5 : 0,
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.rate.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.concessionAmount.toFixed(2)} / {row.concessionPercent.toFixed(0)}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.netAmount.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            <Select
                              fullWidth
                              size="small"
                              value={row.cancelReason}
                              IconComponent={KeyboardArrowDownIcon}
                              onChange={(e) => {
                                const value = e?.target?.value;
                                if (value !== undefined) {
                                  handleChangeReason(row.id, value as string);
                                }
                              }}
                              displayEmpty
                              renderValue={(selected) => {
                                if (!selected) {
                                  return <em style={{ color: '#9CA3AF' }}>{tBilling('invoice.refund.select_reason')}</em>;
                                }
                                return selected as string;
                              }}
                              title={tBilling('invoice.refund.cancel_reason_tooltip')}
                              data-testid={`refund-dialog-cancelReason-${row.id}`}
                              sx={{
                                '& .MuiSelect-select': { py: 1, px: 1.5, fontSize: 14 },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                                borderRadius: 1.5,
                                backgroundColor: '#F9FAFB',
                                opacity: row.refundable ? 1 : 0.8,
                              }}
                              slotProps={{
                                input: {
                                  'aria-label': tBilling('invoice.refund.cancel_reason_label', { name: row.serviceName }),
                                  tabIndex: 0,
                                },
                              }}
                              disabled={!row.refundable}
                            >
                              <MenuItem value="" disabled>
                                <em style={{ color: '#9CA3AF' }}>{tBilling('invoice.refund.select_reason')}</em>
                              </MenuItem>
                              {reasons.map(reason => (
                                <MenuItem key={reason} value={reason}>
                                  {reason}
                                </MenuItem>
                              ))}
                            </Select>
                            {!!inputErrors?.perRow?.[row.id] &&
                        <LiveMessage message={inputErrors.perRow[row.id]} />}
                          </TableCell>
                        </TableRow>
                      );})
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Cancellation remark */}
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                size="small"
                label={tBilling('invoice.refund.remark_label')}
                name="cancellation_remark"
                value={remark}
                onChange={handleRemarkChange}
                multiline
                minRows={2}
                maxRows={3}
                data-testid="refund-dialog-remark"
                slotProps={{
                  htmlInput: {
                    'aria-required': 'true',
                    tabIndex: 0,
                    'aria-label': tBilling('invoice.refund.remark_label'),
                  },
                }}
              />
              <LiveMessage message={inputErrors?.remark || ''} />
            </Box>
          </>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
        <Tooltip title={tBilling('invoice.refund.cancel')} arrow>
          <span>
            <Button
              variant="outlined"
              onClick={onClose}
              data-testid="refund-dialog-cancel"
              sx={{
                flex: 1,
                borderRadius: 2,
                textTransform: 'none',
                py: 1.25,
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
                borderColor: '#E5E7EB',
                '&:hover': { borderColor: '#D1D5DB', backgroundColor: '#F9FAFB' },
              }}
              aria-label={tBilling('invoice.refund.cancel')}
              tabIndex={0}
            >
              {tBilling('invoice.refund.cancel')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            isProceedDisabled
              ? (selectedCount === 0
                ? tBilling('invoice.refund.no_services')
                : selectedRows.some(row => !row.cancelReason)
                  ? tBilling('invoice.refund.reason_required')
                  : '')
              : tBilling('invoice.refund.proceed')
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              onClick={handleProceedClick}
              disabled={isProceedDisabled}
              data-testid="refund-dialog-proceed"
              sx={{
                flex: 1,
                borderRadius: 2,
                textTransform: 'none',
                py: 1.25,
                fontSize: 14,
                fontWeight: 600,
                backgroundColor: '#374151',
                '&:hover': { backgroundColor: '#1F2937' },
                '&.Mui-disabled': { backgroundColor: '#9CA3AF', color: '#fff' },
                transition: 'background-color .2s',
              }}
              aria-label={tBilling('invoice.refund.proceed')}
              tabIndex={0}
              autoFocus={open}
            >
              {refundMutation.isPending ? (
                <CircularProgress size={20} color="inherit" aria-label={tBilling('invoice.refund.loading_label')} />
              ) : (
                tBilling('invoice.refund.proceed')
              )}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={confirmOpen}
        handleClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmRefund}
        type="save"
        title={tBilling('invoice.refund.confirm_refund')}
        contentMessage={tBilling('invoice.refund.confirm_content', { count: selectedCount })}
        isConfirming={refundMutation.isPending}
      />
    </Dialog>
  );
};

export default React.memo(RefundDialog);